import {
    MollieComponentTypeKeys,
    MollieEventPayload
} from "@omni/types/packages/mollie.types";
import { CheckoutStyles } from "@omni/ui/brand/checkout/checkout.styles";
import { input } from "@omni/ui/components/Form/Input/input.style";
import { cn } from "@omni/utils/styles";
import { useCallback, useEffect, useState } from "react";

import { useMollie } from "../mollie.provider";

type SecureInputFieldProps = {
    name: MollieComponentTypeKeys;
    isInvalid: boolean;
    isValid: boolean;
    className?: string;
    onChange?: (e: MollieEventPayload) => void;
    onBlur?: (e: MollieEventPayload) => void;
    onFocus?: (e: MollieEventPayload) => void;
};

const SecureInputField = ({
    name,
    isInvalid,
    isValid,
    className,
    onChange,
    onBlur,
    onFocus
}: SecureInputFieldProps) => {
    const { mollie } = useMollie();
    const [isFocused, setIsFocus] = useState(false);

    const onInnerFocus = useCallback(
        (e: MollieEventPayload) => {
            setIsFocus(true);
            onFocus?.(e);
        },
        [onFocus]
    );

    const onInnerBlur = useCallback(
        (e: MollieEventPayload) => {
            setIsFocus(false);
            onBlur?.(e);
        },
        [onBlur]
    );

    const onInnerChange = useCallback(
        (e: MollieEventPayload) => {
            onChange?.(e);
        },
        [onChange]
    );

    useEffect(() => {
        if (mollie) {
            const cardNumber = mollie.createComponent(name, {
                styles: {
                    base: CheckoutStyles.secureFieldStyles
                }
            });
            cardNumber.mount(`#${name}`);

            cardNumber.addEventListener("change", onInnerChange);
            cardNumber.addEventListener("blur", onInnerBlur);
            cardNumber.addEventListener("focus", onInnerFocus);

            return function cleanUp() {
                cardNumber.unmount();
            };
        }
    }, [mollie, name, onInnerBlur, onInnerChange, onInnerFocus]);

    return (
        <div
            id={name}
            className={cn(
                input({
                    inputType: "default",
                    inputSize: "md",
                    invalid: isInvalid,
                    valid: isValid,
                    focussed: isFocused
                }),
                "tw:read-only:bg-transparent tw:read-only:text-inherit",
                className
            )}
        />
    );
};

export { SecureInputField };
